import React from "react";
import EVENT_ICONS from "../../timeline/components/EventIcons";
import { Text } from "components/common/Text";

interface BasePlayer {
  number: number;
  name: string;
  position?: string;
  subInfo?: { dir: "IN" | "OUT"; minute: number } | null;
  goals?: number;
  assists?: number;
  yellow?: number;
  red?: number;
}

export interface TeamPanelProps {
  teamType: "home" | "away";
  players: {
    starting: BasePlayer[];
    substitutes: BasePlayer[];
  };
  formation: string;
  onFormationChange: (formation: string) => void;
  only?: "starting" | "substitutes";
  hideSectionHeaders?: boolean;
  homeColor: string;
  awayColor: string;
}

const Badges: React.FC<{ p: BasePlayer }> = ({ p }) => {
  const items: React.ReactNode[] = [];
  const push = (icon: React.ReactNode, count?: number) => {
    if (!icon) return;
    items.push(
      <span
        key={`${items.length}`}
        className="inline-flex items-center gap-[0.25vw]"
      >
        {icon}
        {typeof count === "number" && count > 1 ? <span>x{count}</span> : null}
      </span>
    );
  };

  if (p.yellow && p.yellow > 0) push(EVENT_ICONS.yellow_card, p.yellow);
  if (p.red && p.red > 0) push(EVENT_ICONS.red_card, p.red);
  if (p.goals && p.goals > 0) push(EVENT_ICONS.goal, p.goals);
  if (p.assists && p.assists > 0)
    push((EVENT_ICONS as any).assist ?? null, p.assists);

  if (items.length === 0) return null;
  return <span className="inline-flex gap-[0.6vw]">{items}</span>;
};

const TeamPanel: React.FC<TeamPanelProps> = ({
  teamType,
  players,
  only,
  hideSectionHeaders,
  homeColor,
  awayColor,
}) => {
  const renderPlayerRow = (p: BasePlayer, type: "XI" | "SUB") => {
    const showOut = type === "XI" && p.subInfo?.dir === "OUT";
    const showIn = type === "SUB" && p.subInfo?.dir === "IN";

    const nameBlock = (
      <span className="inline-flex flex-row items-center gap-[0.5vw] leading-tight">
        <Text variant="soccerLineupPlayerName">{p.name}</Text>
        {p.position ? (
          <Text variant="soccerLineupPlayerName">{p.position}</Text>
        ) : null}
      </span>
    );

    const badges = <Badges p={p} />;

    const badge =
      showOut || showIn
        ? {
            dir: showOut ? "OUT" : "IN",
            minute: p.subInfo!.minute,
          }
        : null;

    const badgeNode = badge ? (
      <span
        className={`rounded-[0.6vw] border-[0.15vw] px-[0.8vw] py-[0.3vw] text-[1.6vw] font-bold ${
          badge.dir === "IN"
            ? "bg-green/30 text-green border-green"
            : "bg-red/30 text-red border-red"
        } `}
      >
        {badge.dir} {badge.minute}'
      </span>
    ) : null;

    const number = (
      <span
        className="flex h-[4vw] w-[4vw] items-center justify-center rounded-full text-[3vw] font-bold text-white"
        style={{
          backgroundColor: teamType === "home" ? homeColor : awayColor,
          ...(teamType === "home"
            ? { marginLeft: "1vw" }
            : { marginRight: "1vw" }),
          WebkitTextStroke: "0.03vw rgba(0, 0, 0, 0.9)",
          textShadow:
            "calc(-0.12vw) calc(-0.12vw) 0 rgba(0, 0, 0, 0.9), calc(0.1vw) calc(-0.1vw) 0 rgba(0, 0, 0, 0.9), calc(-0.1vw) calc(0.1vw) 0 rgba(0, 0, 0, 0.9), calc(0.1vw) calc(0.1vw) 0 rgba(0, 0, 0, 0.9)",
        }}
      >
        {p.number || "-"}
      </span>
    );

    if (teamType === "home") {
      const farLeftBadge = badgeNode ? (
        <span className="mr-auto">{badgeNode}</span>
      ) : (
        <span className="mr-auto" />
      );

      return (
        <li
          key={`${teamType}-${type}-${p.name}`}
          className="flex items-center justify-end gap-[1vw] py-[1vw] text-right text-[3vw]"
        >
          {farLeftBadge}
          {badges}
          {nameBlock}
          {number}
        </li>
      );
    }

    return (
      <li
        key={`${teamType}-${type}-${p.name}`}
        className="flex items-center justify-start gap-[1vw] py-[1vw] text-left text-[3vw]"
      >
        {number}
        {nameBlock}
        {badges}
        {badgeNode ? <span className="ml-auto">{badgeNode}</span> : null}
      </li>
    );
  };

  const renderStarting = (
    <div className="flex flex-col gap-[1vw]">
      {!hideSectionHeaders && (
        <Text
          variant="soccerLineupTitle"
          className="border-b border-white/10 pb-[1vw] font-bold"
        >
          Starting XI
        </Text>
      )}
      <ul className="m-0 list-none p-0">
        {players.starting.map((p) => renderPlayerRow(p, "XI"))}
      </ul>
    </div>
  );

  const renderSubs = (
    <div className="flex flex-col gap-[1vw]">
      {!hideSectionHeaders && (
        <Text
          variant="soccerLineupTitle"
          className="border-b border-white/10 pb-[1vw] font-bold"
        >
          Substitutes
        </Text>
      )}
      <ul className="m-0 list-none p-0">
        {players.substitutes.map((p) => renderPlayerRow(p, "SUB"))}
      </ul>
    </div>
  );

  return (
    <div className="w-full">
      {only === "starting" && renderStarting}
      {only === "substitutes" && renderSubs}
      {!only && (
        <>
          {renderStarting}
          {renderSubs}
        </>
      )}
    </div>
  );
};

export default TeamPanel;
